import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection } from 'constants/index'
import { signOut } from 'store/auth'
import { toast } from 'react-toastify'
import { first } from 'lodash'
import moment from 'moment'
import { sortDataArrayByCreatedAt } from 'utils/times'

export const initialState = {
  error: null,
  inProgress: false,
  sessions: [],
  session: null
}

const sessionSlice = createSlice({
  name: 'session',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setSessions: (state, action) => {
      state.sessions = action.payload
    },
    setSession: (state, action) => {
      state.session = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.sessions = []
      state.session = null
      state.associatedPolls = []
    }
  }
})

export const addSession = (payload) => async (dispatch, getState) => {
  try {
    dispatch(sessionSlice.actions.setProgress(true))
    const user = getState().auth.user
    const doc = await FirestoreDatabase.collection(Collection.SESSIONS).add({
      ...payload,
      conferenceId: user?.conferenceId,
      createdAt: moment().utc().valueOf()
    })
    dispatch(sessionSlice.actions.setProgress(false))
    toast.success('Session created successfully')
    return doc.id
  } catch (err) {
    dispatch(sessionSlice.actions.setError(err))
    dispatch(sessionSlice.actions.setProgress(false))
  }
}

export const fetchSessionViaPINCode = (pinCode) => async (dispatch) => {
  try {
    const data = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('pinCode', '==', pinCode)
      .limit(1)
      .get()
    const doc = first(data.docs)
    return { id: doc.id, ...doc.data() }
  } catch (err) {
    dispatch(sessionSlice.actions.setError(err))
  }
}

export const fetchSessions = (eventId, conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.SESSIONS)
    .where('eventId', '==', eventId)
    .where('conferenceId', '==', conferenceId)
    .orderBy('startDate')
    .onSnapshot((documents) => {
      const sessions = []
      documents.forEach((doc) => {
        sessions.push({
          id: doc.id,
          ...doc.data(),
          type: 'Session Poll'
        })
      })
      callback(sortDataArrayByCreatedAt(sessions).map((item, i) => ({ ...item, srNo: i + 1 })))
    })
}

export const fetchSessionById = (sessionId) => async (dispatch) => {
  try {
    dispatch(sessionSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SESSIONS).doc(sessionId).get()
    const e = snapshot.data()
    dispatch(
      sessionSlice.actions.setSession({
        id: snapshot.id,
        ...e
      })
    )
    dispatch(sessionSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(sessionSlice.actions.setError(err))
    dispatch(sessionSlice.actions.setProgress(false))
  }
}

export const updateSessionById = (sessionId, payload) => (dispatch) => {
  try {
    dispatch(sessionSlice.actions.setProgress(true))
    FirestoreDatabase.collection(Collection.SESSIONS)
      .doc(sessionId)
      .update({
        ...payload
      })
    dispatch(sessionSlice.actions.setProgress(false))
    toast.success('Session updated successfully')
    return true
  } catch (err) {
    dispatch(sessionSlice.actions.setError(err))
    dispatch(sessionSlice.actions.setProgress(false))
  }
}

export const deleteSessionById = (sessionId) => async (dispatch) => {
  try {
    const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
      .where('sessionId', '==', sessionId)
      .get()

    const talks = talksSnap.docs.map((doc) => {
      return { id: doc?.id, ...doc.data() }
    })

    const pollIdsList = talks.filter((talk) => talk.havePolls === true).map((talk) => talk?.pollId)

    const batch = FirestoreDatabase.batch()
    const pollsColRef = FirestoreDatabase.collection(Collection.POLLS)

    const sessionDoc = await FirestoreDatabase.collection(Collection.SESSIONS).doc(sessionId).get()
    const session = { id: sessionDoc?.id, ...sessionDoc.data() }
    if (session?.havePolls) {
      pollIdsList.push(session?.pollId)
    }

    pollIdsList.forEach((pollId) => {
      const docRef = pollsColRef.doc(pollId)
      batch.update(docRef, {
        alreadyUsed: false,
        eventId: null,
        sessionId: null,
        talkId: null
      })
    })

    talks.forEach((talk) => {
      const talkDocRef = FirestoreDatabase.collection(Collection.TALKS).doc(talk?.id)
      batch.delete(talkDocRef)
    })

    const sessionDocRef = FirestoreDatabase.collection(Collection.SESSIONS).doc(sessionId)
    batch.delete(sessionDocRef)
    await batch.commit()
  } catch (err) {
    dispatch(sessionSlice.actions.setError(err))
  }
}

export const resetSessionStore = () => (dispatch) => {
  try {
    dispatch(sessionSlice.actions.setSession(null))
  } catch (err) {
    dispatch(sessionSlice.actions.setError(err))
  }
}

export default sessionSlice
